/* @flow */

import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Button from '@material-ui/core/Button';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';

import Loading from 'components/Loading/Loading';

import ActionDrawerHeader from '../../../components/ActionDrawerHeader/ActionDrawerHeader';
import AddNewSystemForm from './AddNewSystemForm';
import AddNewSystemSuccess from './AddNewSystemSuccess';
import Form from '../../../../../forms/_.extend';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  formData: Object,
  actionDrawerStore: TypeActionDrawerStore,
  config: Object,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore')
@observer
export default class AddNewRoom extends React.Component<*> {
  @observable vModelData = this.props.model;

  createForm = () => {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore, config }: TypeProps = this.props;
    const fields: Array<Object> = [
      {
        name: 'addSystemForm',
        label: t('general:new_system'),
        title: 'New System',
        fields: [
          {
            name: 'tradingAccount',
            label: t('general:trading_account'),
            rules: 'required|string',
            disabled: true,
            value: this.props.params.id,
          },
          {
            name: 'company',
            label: t('general:company'),
            rules: 'required|string',
            disabled: true,
            value: config.companyId,
          },
          {
            name: 'site',
            label: t('general:locations.labels.site'),
            rules: 'required|string',
            disabled: true,
            value: config.siteId,
          },
          {
            name: 'building',
            label: t('general:locations.labels.building_transport'),
            rules: 'required|string',
            disabled: true,
            value: config.buildingId,
          },
          {
            name: 'room',
            label: t('general:locations.labels.lab_room'),
            rules: 'required|string',
            disabled: true,
            value: config.roomId,
          },
          {
            name: 'systemName',
            label: t('general:locations.labels.system_workspace'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'contactPhoneNo',
            label: t('general:labels.contact_phone_number'),
            rules: 'telephone',
            value: '',
          },
          {
            name: 'contactFaxNo',
            label: t('general:labels.contact_fax_number'),
            rules: 'telephone',
            value: '',
          },
          {
            name: 'locationContact',
            label: t('general:locations.labels.contact'),
            rules: 'string',
            value: '',
          },
          {
            name: 'assetizedLocation',
            label: t('general:assets.labels.assetised_location'),
            rules: 'boolean',
            value: false,
          },
          {
            name: 'assetType',
            label: t('general:labels.type'),
            value: '',
            options: [
              { name: 'Rack' },
              { name: 'Bench' },
              { name: 'Trolley' },
              { name: 'Cabinet' },
              { name: 'Storage Cage' },
              { name: 'Work Cube' },
              { name: 'Desk' },
              { name: 'Van/Truck' },
            ],
          },
          {
            name: 'assetDescription',
            label: t('general:description'),
            value: '',
          },
          {
            name: 'assetNumber',
            label: t('general:assets.labels.number'),
            value: '',
          },
          {
            name: 'assetBarcodeNumber',
            label: t('general:assets.labels.barcode_number'),
            value: '',
          },
          {
            name: 'assetSerialNumber',
            label: t('general:assets.labels.serial_number'),
            value: '',
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess(data: Object) {
        const {
          company,
          room,
          systemName,
          contactPhoneNo,
          locationContact,
          assetType,
          assetizedLocation,
          assetDescription,
          assetNumber,
          assetBarcodeNumber,
          assetSerialNumber,
        }: Object = data.values().addSystemForm;

        const systemData = {
          parent_id: room,
          company_id: company,
          name: systemName,
          country_id: config.countryId,
          contact: locationContact,
          contact_phone: contactPhoneNo,
        };

        if (assetizedLocation) {
          systemData.asset = {
            type: assetType,
            description: assetDescription,
            asset_number: assetNumber,
            barcode_number: assetBarcodeNumber,
            er_serial_number: assetSerialNumber,
          };
        }

        actionDrawerStore.userAddLocation(systemData);
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        options: {
          validateOnInit: true,
          validateOnChange: true,
        },
        name: t('general:new_system'),
      }
    );
  };

  render(): React.Element<*> {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore, config }: TypeProps = this.props;

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={t('general:actions.create_new_system')} />
          <div className={styles['action-drawer-content']}>
            {actionDrawerStore.errorResponse.message}
          </div>
          <div className={styles['action-drawer-actions']}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => actionDrawerStore.closeActionDrawer()}
            >
              {t('general:actions.close')}
            </Button>
          </div>
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return <AddNewSystemSuccess config={config} />;
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={t('general:actions.create_new_system')} />
        <AddNewSystemForm form={this.createForm()} />
      </div>
    );
  }
}
